import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';

import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';

import { RouterModule } from '@angular/router';
import { LinkComponent } from '@terminus-ui/link';
import { MiniFilterComponent } from '@terminus-ui/mini-filter';
import {
  PageHeaderButtonsComponent,
  PageHeaderComponent,
} from '@terminus-ui/page-header';
import { TagComponent } from '@terminus-ui/tag';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DialogService } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { combineLatest, map, startWith, Subject, switchMap, tap } from 'rxjs';
import { TableWrapperComponent } from 'table-wrapper';
import { CustomerDialogComponent } from '../customer-dialog/customer-dialog.component';
import { CustomerService } from '../customer.service';

@Component({
  selector: 'lib-customer-index',
  imports: [
    CommonModule,
    TableWrapperComponent,
    PageHeaderComponent,
    PageHeaderButtonsComponent,
    ButtonModule,
    DialogModule,
    FormsModule,
    ReactiveFormsModule,
    InputTextModule,
    SelectModule,
    MiniFilterComponent,
    TagComponent,
    LinkComponent,
    RouterModule,
  ],
  templateUrl: './customer-index.component.html',
  providers: [DialogService],
})
export class CustomerIndexComponent {
  loading = true;
  statusControl = new FormControl('ANY');
  statusOptions = [
    'ANY',
    'ONBOARDING',
    'ACTIVE',
    'PROSPECT',
    'SUSPENDED',
    'DECOMMISSIONED',
  ];
  tagLabels = {
    ONBOARDING: 'Onboarding',
    ACTIVE: 'Active',
    PROSPECT: 'Prospect',
    SUSPENDED: 'Suspended',
    DECOMMISSIONED: 'Decommissioned',
  };
  tagColors = {
    ONBOARDING: 'blue',
    ACTIVE: 'green',
    PROSPECT: 'yellow',
    SUSPENDED: 'red',
    DECOMMISSIONED: 'grey',
  };
  private customerService = inject(CustomerService);
  private dialogService = inject(DialogService);
  private refreshTable$ = new Subject<boolean>();
  private data$ = this.refreshTable$.pipe(
    startWith(false),
    tap(() => (this.loading = true)),
    switchMap((refresh) =>
      this.customerService.getAllCustomers('ANY', refresh),
    ),
    map((customers) =>
      customers.map((customer) => ({
        ...customer,
        createdDate: customer.createdDate
          ? new Date(customer.createdDate)
          : customer.createdDate,
      })),
    ),
    tap(() => (this.loading = false)),
  );

  filteredData$ = combineLatest([
    this.statusControl.valueChanges.pipe(startWith(this.statusControl.value)),
    this.data$,
  ]).pipe(
    map(([status, customers]) =>
      customers.filter(
        (customer) => status === 'ANY' || customer.status === status,
      ),
    ),
  );

  openNewCustomerForm(): void {
    this.dialogService
      .open(CustomerDialogComponent, {
        header: 'Add New Customer',
        width: '35rem',
        style: { 'min-height': 'fit-content' },
        contentStyle: { overflow: 'visible' },
      })
      .onClose.subscribe((closeValue) => {
        if (closeValue) this.refreshTable$.next(true);
      });
  }
}
